/**
 * GPSR (rozporządzenie UE 2023/988) — dane podmiotu odpowiedzialnego za produkt na terenie UE.
 *
 * Kontrakt spisany ręcznie z odpowiedzi endpointu, bo `products.arte.pl` nie wystawia
 * OpenAPI (`/swagger/*`, `/openapi.json` → 404/302). Wszystkie pola są opcjonalne:
 * API zwraca HTTP 200 również dla kodów, których nie obsługuje — wtedy `available:false`
 * i komplet pól `null`. Patrz issue #1710.
 */
export interface GpsrEconomicOperator
{
  name?: string;
  tradeName?: string;
  addressStreet?: string;
  addressPostalCode?: string;
  addressCity?: string;
  countryCode?: string;
  email?: string;
  phone?: string;
  contactUrl?: string;
}

export interface GpsrResponse
{
  code?: string;
  producerKey?: string;
  lang?: string;
  /** Jedyna wiarygodna bramka „są dane". Statusu HTTP użyć się nie da — zawsze 200. */
  available?: boolean;
  /** Wypełniane, gdy `available:false` (zmierzona wartość: `noData`). */
  reason?: string;
  ownBrand?: boolean;
  manufacturer?: GpsrEconomicOperator | null;
  responsible?: GpsrEconomicOperator | null;
  safetyInformation?: string | null;
  /** Zmierzona wartość: `notImplemented` — informacje o bezpieczeństwie poza zakresem #1710. */
  safetyInformationStatus?: string;
}

/**
 * Podmioty do pokazania w ofercie. Art. 19 GPSR wymaga DWÓCH ról, nie jednej:
 *
 *   (a) producent — ZAWSZE,
 *   (b) osoba odpowiedzialna w UE — DODATKOWO, gdy producent nie ma siedziby w UE.
 *
 * To nie jest alternatywa: przy producencie spoza UE trzeba pokazać oba podmioty.
 * Wcześniejsza reguła `responsible ?? manufacturer` wybierała jeden i dla towaru
 * importowanego pokazywałaby za mało (a import jest w tym katalogu normą — patrz
 * parametr „Kraj pochodzenia").
 */
export interface GpsrView
{
  manufacturer: GpsrEconomicOperator | null;
  euResponsible: GpsrEconomicOperator | null;
}

/**
 * Buduje widok z odpowiedzi API, albo zwraca `null` gdy nie ma czego pokazać.
 *
 * O tym, czy dochodzi blok „osoba odpowiedzialna w UE", decyduje WYŁĄCZNIE obecność
 * pola `responsible` w odpowiedzi. Front celowo NIE kwalifikuje sam, czy kraj producenta
 * należy do UE: to ocena prawna, a nie zadanie dla listy kodów krajów zaszytej w kodzie
 * frontu. Jeśli producent jest spoza UE, a `responsible` jest puste — to luka w danych
 * po stronie API i tak też ją raportujemy (patrz GpsrService), zamiast ją zgadywać.
 *
 * Czysta funkcja, świadomie bez zależności od Angulara: to reguła decydująca
 * o publikacji treści wymaganej prawem i ma być testowalna bez DOM-u.
 */
export function selectGpsrView(response: GpsrResponse | null | undefined): GpsrView | null
{
  if (!response || response.available !== true)
  {
    return null;
  }

  const manufacturer = hasName(response.manufacturer) ? response.manufacturer : null;
  const euResponsible = hasName(response.responsible) ? response.responsible : null;

  if (!manufacturer && !euResponsible)
  {
    return null;
  }

  return { manufacturer, euResponsible };
}

function hasName(operator: GpsrEconomicOperator | null | undefined): boolean
{
  return !!operator && !!operator.name;
}

/**
 * Miejscowość z kodem pocztowym: „42-202 Częstochowa".
 * Puste fragmenty wypadają, żeby nie zostawiać samego kodu ani wiszącej spacji.
 */
export function formatGpsrCity(operator: GpsrEconomicOperator | null): string
{
  if (!operator)
  {
    return '';
  }

  return [operator.addressPostalCode, operator.addressCity].filter((part) => !!part).join(' ');
}

/**
 * Kod kraju na polską nazwę: `DE` → „Niemcy", `PL` → „Polska".
 *
 * Przez `Intl.DisplayNames`, a nie własną mapę: mapa kodów ISO zaszyta w kodzie
 * to kilkaset pozycji, które trzeba by utrzymywać, a przeglądarka ma te dane
 * u siebie. Gdy API nie jest dostępne albo kod jest nieznany, zostaje sam kod —
 * lepszy niż puste miejsce w adresie wymaganym prawem.
 */
export function formatGpsrCountry(countryCode: string | null | undefined): string
{
  if (!countryCode)
  {
    return '';
  }

  try
  {
    const displayNames = new Intl.DisplayNames(['pl'], { type: 'region' });

    return displayNames.of(countryCode.toUpperCase()) || countryCode;
  }
  catch (error)
  {
    return countryCode;
  }
}

/**
 * Adres w jednej linii, tak jak w projekcie:
 * „Computacenter Park 3-4, 50170 Kerpen, Niemcy".
 */
export function formatGpsrPostalAddress(operator: GpsrEconomicOperator | null): string
{
  if (!operator)
  {
    return '';
  }

  return [operator.addressStreet, formatGpsrCity(operator), formatGpsrCountry(operator.countryCode)]
    .filter((part) => !!part)
    .join(', ');
}

/**
 * Pełny adres w jednej linii: „ul. Legionów 101, 42-202 Częstochowa".
 *
 * W modalu adres jest rozbity na dwie linie („Adres" i „Miasto"), ale ta postać
 * zostaje: służy sprawdzeniu, czy adres pocztowy w ogóle jest — a tego wymaga
 * art. 19 lit. a. Patrz {@link missingMandatoryManufacturerFields}.
 */
export function formatGpsrAddress(operator: GpsrEconomicOperator | null): string
{
  if (!operator)
  {
    return '';
  }

  return [operator.addressStreet, formatGpsrCity(operator)].filter((part) => !!part).join(', ');
}

/**
 * Pola, których art. 19 GPSR wymaga dla producenta, a których w odpowiedzi brakuje:
 * nazwa oraz adres **pocztowy i elektroniczny** (e-mail albo strona www).
 *
 * Puste pole i tak nie zostanie wyrenderowane, więc bez tego sprawdzenia brak danej
 * wymaganej prawem przechodziłby zupełnie bezgłośnie. Zwracamy listę, żeby dało się
 * ją zaraportować — decyzja, co z tym zrobić, należy do wołającego.
 */
export function missingMandatoryManufacturerFields(operator: GpsrEconomicOperator | null): string[]
{
  if (!operator)
  {
    return ['manufacturer'];
  }

  const missing: string[] = [];

  if (!operator.name)
  {
    missing.push('name');
  }

  if (!formatGpsrAddress(operator))
  {
    missing.push('adres pocztowy');
  }

  if (!operator.email && !operator.contactUrl)
  {
    missing.push('adres elektroniczny');
  }

  return missing;
}
